
import { useState } from 'react';

const Home = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      title: "Fulfilment Centre",
      description: "Smart storage, distribution, and real-time tracking for seamless order management.",
      icon: "ri-truck-line",
      features: ["Real-time inventory tracking", "Automated order processing", "Global shipping network", "24/7 warehouse monitoring"]
    },
    {
      title: "AI Marketing & Funnels",
      description: "Viral video hooks, social media content, and landing pages powered by artificial intelligence.",
      icon: "ri-robot-line",
      features: ["AI-generated video content", "Automated social media posts", "Conversion-optimized funnels", "Performance analytics"]
    },
    {
      title: "E-Commerce Solutions",
      description: "Complete online store setup and order management systems for modern businesses.",
      icon: "ri-shopping-cart-line",
      features: ["Custom store development", "Payment gateway integration", "Order management system", "Mobile-responsive design"]
    }
  ];

  const caseStudies = [
    {
      client: "TechStart Malaysia",
      industry: "Electronics",
      growth: "340%",
      metric: "Revenue Growth",
      description: "Transformed their fulfilment process and boosted online sales through AI-driven marketing campaigns.",
      image: "https://public.readdy.ai/ai/img_res/445786759161d7f996b5921e6f8a4cd7.jpg"
    },
    {
      client: "Fashion Forward",
      industry: "Apparel",
      growth: "250%",
      metric: "Order Volume",
      description: "Streamlined their e-commerce operations and created viral marketing content that drove massive engagement.",
      image: "https://readdy.ai/api/search-image?query=modern%20fashion%20e-commerce%20workspace%2C%20stylish%20clothing%20displays%2C%20digital%20marketing%20screens%2C%20contemporary%20office%20environment%2C%20clean%20white%20background%2C%20professional%20lighting&width=400&height=300&seq=case2&orientation=landscape"
    },
    {
      client: "HealthTech Solutions",
      industry: "Healthcare",
      growth: "180%",
      metric: "Customer Acquisition",
      description: "Implemented AI marketing funnels and optimized their fulfilment chain for medical device distribution.",
      image: "https://readdy.ai/api/search-image?query=healthcare%20technology%20distribution%20center%2C%20medical%20devices%20warehouse%2C%20clean%20sterile%20environment%2C%20modern%20logistics%20systems%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20photography&width=400&height=300&seq=case3&orientation=landscape"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      position: "CEO, TechStart Malaysia",
      content: "36zero transformed our entire business model. Their AI marketing solutions increased our conversion rates by 340% while their fulfilment centre handled our explosive growth seamlessly.",
      avatar: "https://readdy.ai/api/search-image?query=professional%20asian%20businesswoman%20CEO%20portrait%2C%20confident%20smile%2C%20modern%20office%20background%2C%20business%20attire%2C%20clean%20professional%20photography%20style%2C%20minimal%20background&width=80&height=80&seq=avatar1&orientation=squarish"
    },
    {
      name: "Marcus Rodriguez",
      position: "Founder, Fashion Forward",
      content: "The team at 36zero doesn't just provide services - they become your growth partner. Their e-commerce solutions and viral marketing strategies took us from startup to market leader.",
      avatar: "https://readdy.ai/api/search-image?query=professional%20hispanic%20businessman%20founder%20portrait%2C%20confident%20expression%2C%20modern%20office%20setting%2C%20business%20casual%20attire%2C%20clean%20photography%20style%2C%20minimal%20background&width=80&height=80&seq=avatar2&orientation=squarish"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="text-2xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
                36zero
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Services</a>
              <a href="#case-studies" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Case Studies</a>
              <a href="#about" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">About</a>
              <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Contact</a>
              <button className="bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                Get Started
              </button>
            </div>
            <div className="md:hidden">
              <button className="text-gray-700 hover:text-blue-600 cursor-pointer">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-menu-line text-xl"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50" 
               style={{
                 backgroundImage: `url('https://readdy.ai/api/search-image?query=futuristic%20business%20ecosystem%20visualization%2C%20warehouse%20logistics%20flow%20connecting%20to%20digital%20marketing%20screens%20and%20e-commerce%20dashboards%2C%20modern%20technology%20integration%2C%20clean%20white%20and%20blue%20color%20scheme%2C%20professional%203D%20rendering%20style%2C%20minimal%20background%20with%20subtle%20geometric%20patterns&width=1920&height=1080&seq=hero1&orientation=landscape')`,
                 backgroundSize: 'cover',
                 backgroundPosition: 'center',
                 backgroundRepeat: 'no-repeat'
               }}>
        <div className="absolute inset-0 bg-white/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Empowering Businesses,
              <span className="text-blue-600"> 360 Degrees.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed">
              From storage to storytelling — we handle fulfilment, marketing, and e-commerce 
              so you can focus on growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button className="bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer">
                Get Started
              </button>
              <button className="border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-900 hover:text-white transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer">
                Explore Services
              </button>
            </div>
          </div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-600/10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-blue-600/5 rounded-full animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-20 w-16 h-16 bg-blue-600/10 rounded-full animate-pulse delay-500"></div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Complete Business Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our integrated ecosystem covers every aspect of your business growth, 
              from warehouse to web presence.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`relative p-8 rounded-2xl border-2 transition-all duration-300 cursor-pointer transform hover:scale-105 ${
                  activeService === index 
                    ? 'border-blue-600 bg-blue-50 shadow-xl' 
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-lg'
                }`}
                onClick={() => setActiveService(index)}
              >
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                    activeService === index ? 'bg-blue-600' : 'bg-gray-100'
                  }`}>
                    <i className={`${service.icon} text-2xl ${
                      activeService === index ? 'text-white' : 'text-gray-600'
                    }`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
                
                {activeService === index && (
                  <div className="mt-6 space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm text-gray-700">
                        <div className="w-4 h-4 flex items-center justify-center mr-3">
                          <i className="ri-check-line text-blue-600"></i>
                        </div>
                        {feature}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Ecosystem Visualization */}
          <div className="relative bg-gradient-to-r from-blue-50 to-gray-50 rounded-3xl p-12 overflow-hidden">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">The 36zero Ecosystem</h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                See how our integrated solutions work together to create a seamless business operation.
              </p>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=business%20ecosystem%20flow%20diagram%20showing%20warehouse%20logistics%20connecting%20to%20AI%20marketing%20tools%20and%20e-commerce%20platforms%2C%20modern%20infographic%20style%2C%20clean%20blue%20and%20white%20design%2C%20professional%20illustration%20with%20arrows%20and%20connections%2C%20minimal%20background&width=1200&height=600&seq=ecosystem1&orientation=landscape"
                alt="36zero Ecosystem"
                className="w-full h-auto rounded-2xl shadow-lg object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real businesses. See how 36zero has transformed 
              companies across various industries.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={study.image}
                    alt={study.client}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="text-sm opacity-90">{study.industry}</div>
                    <div className="text-lg font-bold">{study.client}</div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="text-center mb-4">
                    <div className="text-4xl font-bold text-blue-600 mb-1">{study.growth}</div>
                    <div className="text-sm text-gray-600 uppercase tracking-wide">{study.metric}</div>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{study.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="bg-white rounded-3xl p-12">
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">What Our Clients Say</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="relative">
                  <div className="bg-gray-50 rounded-2xl p-8">
                    <div className="flex items-center mb-6">
                      <img 
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover object-top mr-4"
                      />
                      <div>
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-600">{testimonial.position}</div>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed italic">"{testimonial.content}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to take your business full circle?
          </h2>
          <p className="text-xl text-blue-100 mb-12 leading-relaxed">
            Join hundreds of businesses that have transformed their operations with 36zero. 
            Let's discuss how we can accelerate your growth.
          </p>
          <button className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer">
            Book a Free Strategy Call
          </button>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/5 rounded-full"></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-white/5 rounded-full"></div>
        <div className="absolute top-1/2 right-20 w-16 h-16 bg-white/5 rounded-full"></div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="text-3xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                36zero
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Empowering businesses with complete solutions for fulfilment, 
                marketing, and e-commerce. Your growth is our mission.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-tiktok-line text-lg"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Fulfilment Centre</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">AI Marketing</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">E-Commerce Solutions</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Strategy Consulting</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Case Studies</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Careers</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 36zero. All rights reserved.
            </div>
            <div className="flex items-center space-x-6">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
